import React, { useRef } from "react";
import { useLang } from "@/contexts/LanguageContext";
import { gsap, useGsap } from "@/lib/motion";

/* Proof as one sentence, not a stat grid: the numbers are part of the story.
   Each figure counts up as the line scrolls into view. */
function Figure({ value }) {
  return (
    <span className="proof-num type-num type-wide font-black text-[var(--signal)] whitespace-nowrap" data-value={value}>
      {value}
    </span>
  );
}

export default function ProofSection() {
  const { t, lang } = useLang();
  const p = t.proof;
  const root = useRef(null);

  useGsap(
    () => {
      root.current.querySelectorAll(".proof-num").forEach((el) => {
        const raw = el.dataset.value || "";
        const match = raw.match(/[\d.,]+/);
        if (!match) return;
        const sep = /\d[.,]\d{3}/.test(match[0]) ? match[0].replace(/\d/g, "")[0] : "";
        const target = parseInt(match[0].replace(/[.,]/g, ""), 10);
        const [before, after] = raw.split(match[0]);
        const fmt = (n) => {
          const s = String(Math.round(n));
          return sep ? s.replace(/\B(?=(\d{3})+(?!\d))/g, sep) : s;
        };
        const obj = { n: 0 };
        gsap.to(obj, {
          n: target,
          duration: 2,
          ease: "expo.out",
          onUpdate: () => {
            el.textContent = `${before}${fmt(obj.n)}${after}`;
          },
          scrollTrigger: { trigger: el, start: "top 85%", once: true },
        });
      });

      gsap.from(".proof-line", {
        opacity: 0.12,
        stagger: 0.1,
        ease: "none",
        scrollTrigger: { trigger: root.current, start: "top 80%", end: "center 55%", scrub: true },
      });
    },
    [lang],
    root,
  );

  const parts = [
    p.before && `${p.before} `,
    <Figure key="y" value={p.years} />,
    p.mid1 && ` ${p.mid1} `,
    <Figure key="s" value={p.shipments} />,
    ` ${p.mid2} `,
    <Figure key="r" value={p.routes} />,
    ` ${p.mid3} `,
    <Figure key="c" value={p.clients} />,
    ` ${p.after}`,
  ].filter(Boolean);

  return (
    <section ref={root} data-testid="proof-section" className="on-dark relative overflow-hidden bg-[var(--forest-800)]">
      <div aria-hidden className="absolute inset-0 dot-grid opacity-50" />
      <div className="container-x relative py-24 lg:py-36">
        <p className="proof-line type-display text-[clamp(1.9rem,4.6vw,4rem)] leading-[1.12] text-[var(--on-dark)] max-w-[22ch]">
          {parts}
        </p>
        <div className="proof-line mt-12 flex items-center gap-4">
          <span className="type-num type-wide text-[clamp(2.4rem,5vw,4.5rem)] font-black text-[var(--on-dark)]">99.2%</span>
          <span className="h-px flex-1 max-w-[12rem] bg-white/25" aria-hidden />
          <span className="text-lg text-[var(--on-dark-muted)]">{p.ontime}</span>
        </div>
      </div>
    </section>
  );
}
